export interface ComplementaryServiceCategoryState {
    Id: string;
    Name: string;
    Key: string;
    Description: string;
    Icon: string;
    ForRoom: boolean;
    ForHotel: boolean;
}

export interface ComplementaryHotelImageTypeState {
    Id: string;
    Name: string;
    Key: string;
    Icon: string;
}

export interface ComplementaryServiceAttributeState {
    Id: string;
    Name: string;
    Key: string;
    IsBoolean: boolean;
    IsDate: boolean;
    IsNumeric: boolean;
    IsString: boolean;
}

export interface ServiceCategoryState
    extends ComplementaryServiceCategoryState {
    TotalServices: number;
    SelectedServices: number;
}

export interface ComplementaryServiceTypeState {
    Id: string;
    Name: string;
    IdServiceCategory: number;
    IsNewService: boolean;
    ForRoom: boolean;
    ForHotel: boolean;
}

export interface ComplementaryPublishStatusState {
    Id: string;
    Name: string;
    Key: string;
}

export interface ComplementaryExceptionLevelTypeState {
    Id: string;
    Name: string;
    Key: string;
}

export interface ComplementaryLanguageState {
    Id: string;
    Name: string;
    Code: string;
}

export enum AttributeDefinitionFieldType {
    BOOLEAN = 'Boolean',
    STRING = 'String',
    NUMBER = 'Numeric',
    DATE = 'Date',
}

export enum AttributeDefinitionSizeKeyType {
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    LARGE = 'LARGE',
}

export interface AttributeDefinitionNameTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Name: string;
}

export interface AttributeDefinitionState {
    Id: string;
    IdAttributeDefinition: number;
    Required: boolean;
    FieldType: AttributeDefinitionFieldType;
    Name: string;
    NameTranslations: AttributeDefinitionNameTranslationState[];
    Unique: boolean;
    SizeKey: AttributeDefinitionSizeKeyType;
    IdCategory: number;
    Category: string;
    CharacterLimit: number;
}

export interface ExceptionServiceDateState {
    Id: string;
    Active: boolean;
    FromDate: Date | string;
    ToDate: Date | string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ExceptionServiceDescriptionTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Description: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ExceptionServiceState {
    Id: string;
    IdHotelService: number;
    Active: boolean;
    Description: string;
    Dates: ExceptionServiceDateState[];
    IdServiceExceptionLevelType: number;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ValueContentTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Value: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface AttributeValueState {
    Id: string;
    IdAttributeDefinition: number;
    Value: string;
    IsString: boolean;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ServiceTypeTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Name: string;
}

export interface ServiceCategoryTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Name: string;
}

export interface RoomServiceDescriptionTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Description: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ZinantisServiceBaseState {
    Id: string;
    IdServiceType: number;
    IdServiceCategory: number;
    ServiceType: string;
    ServiceCategory: string;
    Description: string;
    ServiceTypeTranslations?: ServiceTypeTranslationState[];
    ServiceCategoryTranslations?: ServiceCategoryTranslationState[];
    AttributeDefinitions?: AttributeDefinitionState[];
    AttributeValues?: AttributeValueState[];
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export type ZinantisRoomServiceState = ZinantisServiceBaseState;

export interface ZinantisServiceState extends ZinantisServiceBaseState {
    Name: string;
    Exceptions: ExceptionServiceState[];
}

interface ContractTypeTranslationState {
    Id: string;
    IdLanguage: number;
    LanguageCode: string;
    Name: string;
}

export interface URLState {
    Id: string;
    Enabled: boolean;
    IsPrimary: boolean;
    Path: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export enum URLAvailabilityStatus {
    AVAILABLE = 'available',
    NOT_AVAILABLE = 'not-available',
}

export interface SuggestedURLState {
    path: string;
    status: URLAvailabilityStatus;
}

export interface HotelImageState {
    Id: string;
    IdCategory: number;
    Category: string;
    CategoryKey: string;
    Url: string;
    Caption: string;
    Feature: boolean;
    Published: boolean;
    IdHotelRoomType: number;
    IdSupplierHotelRoomType: number;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface LocalHotelImageState extends HotelImageState {
    File: File | null;
}

export interface SupplierHotelRoomState {
    Id: string;
    Enabled: boolean;
    IdSupplierHotelRoom: number;
    SupplierHotelRoom: string;
    MappingCode: string;
}

export interface HotelRoomState {
    Id: string;
    IdSupplierHotelRoom: number;
    MasterRoomName: string;
    Label: string;
    MainSupplierHotelMappingCode: string;
    MainSupplierHotelRoomName: string;
    HotelImages: HotelImageState[];
    SupplierHotelRooms: SupplierHotelRoomState[];
    Services: ZinantisRoomServiceState[];
    __isChanged?: boolean;
}

export enum ActivityLogTypeKey {
    CATALOGUE = 'CATALOGUE',
    ERRATAS = 'ERRATAS',
    OTHER = 'OTHER',
}

export interface ActivityLogState {
    Id: string;
    Agency: string;
    DateCreated: Date;
    Failed: boolean;
    FailedMessage: string;
    Message: string;
    TypeKey: ActivityLogTypeKey;
    Type: string;
}

export interface AgencyConnectionState {
    name: string;
    failed: boolean;
    lastConnection: Date | string | null;
}

export interface AgencyTypeLogState {
    typeKey: ActivityLogTypeKey;
    last: ActivityLogState;
    lastSuccessful?: ActivityLogState;
}

export interface AgencyLogGroupState {
    agency: string;
    types: AgencyTypeLogState[];
    lastConnection: Date | null;
}

export enum ConnectivityLogsSortKey {
    AGENCY_NAME = 'AGENCY_NAME',
    LAST_CONNECTION = 'LAST_CONNECTION',
}

export enum ConnectivityLogsSortDirection {
    ASC = 'ASC',
    DESC = 'DESC',
}

export interface ConnectivityLogsSortState {
    key: ConnectivityLogsSortKey;
    direction: ConnectivityLogsSortDirection;
}

export interface FileState {
    Caption: string;
    VirtualPath: string;
}

export interface ZitoneoScrapingResultState {
    Id: string;
    ExecutionDate: Date;
    DateLastUpdated: Date;
    Result: boolean;
    IdProcess: number;
    Process: string;
    ProcessStatusKey: string;
    ProcessStatus: string;
    IdSection: string;
    IdFile: number;
    File: string;
    FileDescription: string;
    ZitoneoScrapingExecutionTypeKey: string;
}

export interface HotelContentState {
    Id: string;
    IdHotel: number;
    Name: string;
    HotelOwner: string;
    Address: string;
    City: string;
    Country: string;
    LastChange: Date | string;
    LastChangeBy: string;
    GiataCode: string;
    Rooms: number;
    Category: string;
    CategoryKey: string;
    ContractType: {
        Name: string;
        Translations: ContractTypeTranslationState[];
    };
    ZitoneoPublishStatus: string;
    ZitoneoPublishStatusKey: string;
    ZitoneoPublishStatusColor: string;
    HotelServices: ZinantisServiceState[];
    URLs: URLState[];
    ZitoneoMemberDate: Date | string;
    ZitoneoMembership: boolean;
    HotelImages: HotelImageState[];
    HotelRoomTypes: HotelRoomState[];
    ActivityLogs: ActivityLogState[];
    IdLogoFile: number;
    LogoFile: FileState;
    IdIconFile: number;
    IconFile: FileState;
    ZitoneoScrapingResults: ZitoneoScrapingResultState[];
}

export enum ZitoneoViewMode {
    EDITOR = 'editor',
    PREVIEW = 'preview',
    SETTINGS = 'settings',
}

export enum ZitoneoPreviewMode {
    MOBILE = 'mobile',
    TABLET = 'tablet',
    DESKTOP = 'desktop',
}

export enum AvailableEditionLanguages {
    EN = 'en',
}

export enum VisibleContentScope {
    ROOM = 'room',
    HOTEL = 'hotel',
}

export enum ZitoneoContentType {
    SERVICES = 'services',
    IMAGES = 'images',
}
